import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { logger } from "@/lib/logger"
import { stringArraySchema } from "@/utils/validation/externalBoundarySchemas"
import { warnOnInvalidExternalData } from "@/utils/validation/externalBoundaryValidation"

const log = logger("ConversationsNavigationContext")

interface ConversationsNavigationContextType {
  simulationOutputIds: string[]
  currentIndex: number
  setNavigationData: (ids: string[], currentId: string) => void
  setCurrentSimulationOutputId: (currentId: string) => void
  getNextId: () => string | undefined
  getPreviousId: () => string | undefined
  hasNext: boolean
  hasPrevious: boolean
}

const ConversationsNavigationContext = createContext<
  ConversationsNavigationContextType | undefined
>(undefined)

function readSimulationOutputIds(key: string | undefined): string[] {
  if (typeof window === "undefined" || !key) {
    return []
  }
  const stored = localStorage.getItem(key)
  return stored
    ? warnOnInvalidExternalData(
        JSON.parse(stored) as string[],
        stringArraySchema,
        {
          boundary: "Uploaded conversation navigation IDs",
          log,
        },
      )
    : []
}

function readCurrentIndex(key: string | undefined): number {
  if (typeof window === "undefined" || !key) {
    return -1
  }
  const stored = localStorage.getItem(key)
  return stored ? parseInt(stored, 10) : -1
}

export const ConversationsNavigationProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  // Scoped by org + workspace so a workspace switch can't resume prev/next
  // navigation over another workspace's conversation ids.
  const idsStorageKey = useScopedStorageKey("monitoring-navigation-ids")
  const indexStorageKey = useScopedStorageKey(
    "monitoring-navigation-current-index",
  )

  const [simulationOutputIds, setSimulationOutputIds] = useState<string[]>(() =>
    readSimulationOutputIds(idsStorageKey),
  )

  const [currentIndex, setCurrentIndex] = useState<number>(() =>
    readCurrentIndex(indexStorageKey),
  )

  // Re-hydrate when the active org/workspace changes scope, not just once
  // on mount — this provider can outlive a client-side workspace switch.
  const initializedForKeyRef = useRef<string | undefined>(idsStorageKey)
  useEffect(() => {
    if (initializedForKeyRef.current === idsStorageKey) {
      return
    }
    initializedForKeyRef.current = idsStorageKey
    setSimulationOutputIds(readSimulationOutputIds(idsStorageKey))
    setCurrentIndex(readCurrentIndex(indexStorageKey))
  }, [idsStorageKey, indexStorageKey])

  const setNavigationData = useCallback(
    (ids: string[], currentId: string) => {
      setSimulationOutputIds(ids)
      const index = ids.indexOf(currentId)
      setCurrentIndex(index)

      // Store in localStorage for persistence across page navigation
      if (typeof window !== "undefined") {
        if (idsStorageKey) {
          localStorage.setItem(idsStorageKey, JSON.stringify(ids))
        }
        if (indexStorageKey) {
          localStorage.setItem(indexStorageKey, index.toString())
        }
      }
    },
    [idsStorageKey, indexStorageKey],
  )

  const setCurrentSimulationOutputId = useCallback(
    (currentId: string) => {
      const index = simulationOutputIds.indexOf(currentId)
      if (index !== -1) {
        setCurrentIndex(index)
        if (typeof window !== "undefined" && indexStorageKey) {
          localStorage.setItem(indexStorageKey, index.toString())
        }
      }
    },
    [indexStorageKey, simulationOutputIds],
  )

  const getNextId = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < simulationOutputIds.length - 1) {
      return simulationOutputIds[currentIndex + 1]
    }
    return undefined
  }, [simulationOutputIds, currentIndex])

  const getPreviousId = useCallback(() => {
    if (currentIndex > 0) {
      return simulationOutputIds[currentIndex - 1]
    }
    return undefined
  }, [simulationOutputIds, currentIndex])

  const hasNext =
    currentIndex >= 0 && currentIndex < simulationOutputIds.length - 1
  const hasPrevious = currentIndex > 0

  const value = React.useMemo(
    () => ({
      simulationOutputIds,
      currentIndex,
      setNavigationData,
      setCurrentSimulationOutputId,
      getNextId,
      getPreviousId,
      hasNext,
      hasPrevious,
    }),
    [
      simulationOutputIds,
      currentIndex,
      setNavigationData,
      setCurrentSimulationOutputId,
      getNextId,
      getPreviousId,
      hasNext,
      hasPrevious,
    ],
  )

  return (
    <ConversationsNavigationContext.Provider value={value}>
      {children}
    </ConversationsNavigationContext.Provider>
  )
}

export const useConversationsNavigation = () => {
  const context = useContext(ConversationsNavigationContext)
  if (!context) {
    throw new Error(
      "useConversationsNavigation must be used within ConversationsNavigationProvider",
    )
  }
  return context
}
